import React, { useEffect, useState } from "react";
import { continueRender, delayRender, staticFile, spring, interpolate } from "remotion";

/* ════════════════════════════════════════════════════════════════════════
   TEMA NEOBRUTALISM — palet warna, font, dan helper animasi.
   Semua scene memakai konstanta di file ini supaya konsisten.
   ════════════════════════════════════════════════════════════════════════ */

export const INK = "#111111";
export const YELLOW = "#FFE14D";
export const CYAN = "#22E0FF";
export const PINK = "#FF2E88";
export const PURPLE = "#B026FF";
export const LIME = "#B8FF2F";
export const RED = "#FF3B30";
export const WHITE = "#FFFFFF";
export const CREAM = "#FFFDF5";
export const WA_GREEN = "#25D366";

/** Font display tebal (headline uppercase ekstrem). */
export const DISPLAY = "'Archivo Black', 'DejaVu Sans', Arial, sans-serif";
/** Font UI / body. */
export const UI = "Onest, 'DejaVu Sans', Arial, sans-serif";

/* ── Font loading (delayRender → tidak ada frame dengan fallback font) ── */

const FONTS: Array<[string, string, string]> = [
  ["Archivo Black", "400", "archivo-black.woff2"],
  ["Onest", "400", "onest-400.woff2"],
  ["Onest", "700", "onest-700.woff2"],
  ["Onest", "800", "onest-800.woff2"],
];

let fontPromise: Promise<unknown> | null = null;

const loadFonts = () => {
  if (fontPromise) {
    return fontPromise;
  }
  fontPromise = Promise.all(
    FONTS.map(async ([family, weight, file]) => {
      const face = new FontFace(family, `url(${staticFile(`fonts/${file}`)}) format("woff2")`, {
        weight,
        style: "normal",
      });
      const loaded = await face.load();
      document.fonts.add(loaded);
    }),
  );
  return fontPromise;
};

export const useAdFonts = () => {
  const [handle] = useState(() => delayRender("Memuat font iklan (Archivo Black + Onest)"));
  useEffect(() => {
    loadFonts()
      .then(() => continueRender(handle))
      .catch(() => continueRender(handle));
  }, [handle]);
};

/* ── Preset spring: snappy, bouncy, explosive ── */

export const SNAPPY = { damping: 14, stiffness: 260, mass: 0.7 };
export const BOUNCY = { damping: 9, stiffness: 130, mass: 0.9 };
export const EXPLOSIVE = { damping: 11, stiffness: 320, mass: 0.9 };
export const SETTLE = { damping: 20, stiffness: 90, mass: 1 };

export type Preset = typeof SNAPPY;

/**
 * Pop-in khas neobrutalism: skala dari 0 → overshoot → 1.
 * `delay` dalam frame (relatif terhadap Sequence scene).
 */
export const popIn = (
  frame: number,
  fps: number,
  delay = 0,
  config: Preset = SNAPPY,
): { scale: number; opacity: number } => {
  const scale = spring({ frame: frame - delay, fps, config });
  const opacity = interpolate(frame, [delay, delay + 4], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return { scale, opacity };
};

/** Slide dari bawah + sedikit rotasi, untuk panel/kartu besar. */
export const slideUp = (
  frame: number,
  fps: number,
  delay = 0,
  distance = 1100,
  config: Preset = EXPLOSIVE,
): number => {
  const p = spring({ frame: frame - delay, fps, config });
  return interpolate(p, [0, 1], [distance, 0]);
};

/** Goyangan panik (Scene 1): dua gelombang sinus berbeda frekuensi. */
export const frantic = (frame: number, amount = 1) => ({
  rot: (Math.sin(frame / 2.1) * 2.6 + Math.sin(frame / 5.3) * 1.8) * amount,
  x: (Math.sin(frame / 3.4) * 9 + Math.sin(frame / 1.7) * 4) * amount,
  y: (Math.cos(frame / 2.7) * 7 + Math.sin(frame / 6.1) * 3) * amount,
});

/** Pulang-pulik terus-menerus (CTA): 1 ± amplitude. */
export const pulse = (frame: number, periodInFrames = 45, amplitude = 0.05) =>
  1 + Math.sin((frame / periodInFrames) * Math.PI * 2) * amplitude;

/* ── Komponen dasar neobrutalism (Tailwind) ── */

export const SHADOW = "shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]";
export const SHADOW_LG = "shadow-[14px_14px_0px_0px_rgba(0,0,0,1)]";

export const NeoCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  big?: boolean;
}> = ({ children, className = "", style, big = false }) => (
  <div
    className={`border-4 border-black ${big ? SHADOW_LG : SHADOW} ${className}`}
    style={style}
  >
    {children}
  </div>
);

/** Chip label kecil uppercase. */
export const Chip: React.FC<{
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}> = ({ children, className = "", style }) => (
  <div
    className={`inline-block border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] px-5 py-2 font-black uppercase tracking-wider ${className}`}
    style={{ fontFamily: DISPLAY, ...style }}
  >
    {children}
  </div>
);

/** Latar scene + pola khas neobrutalism. */
export const Bg: React.FC<{
  color: string;
  pattern?: "dots" | "stripes" | "grid" | "none";
  children: React.ReactNode;
  patternColor?: string;
  patternOpacity?: number;
}> = ({ color, pattern = "dots", children, patternColor = INK, patternOpacity = 0.12 }) => {
  const style: React.CSSProperties =
    pattern === "dots"
      ? {
          backgroundImage: `radial-gradient(circle, ${patternColor} 2.5px, transparent 2.5px)`,
          backgroundSize: "46px 46px",
        }
      : pattern === "stripes"
        ? {
            backgroundImage: `repeating-linear-gradient(45deg, ${patternColor} 0 14px, transparent 14px 42px)`,
          }
        : pattern === "grid"
          ? {
              backgroundImage: `linear-gradient(${patternColor} 2px, transparent 2px), linear-gradient(90deg, ${patternColor} 2px, transparent 2px)`,
              backgroundSize: "64px 64px",
            }
          : {};

  return (
    <div className="absolute inset-0" style={{ background: color }}>
      <div className="absolute inset-0" style={{ ...style, opacity: patternOpacity }} />
      <div className="absolute inset-0">{children}</div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
